import { CheckCircle2, Circle, Loader2, FilePlus2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { PackagingItem } from '@/shared/hooks/api/packagingSuppliers'
import { useCanWriteOrgData } from '@/shared/hooks/useCanWriteOrgData'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'
import {
  evaluatePackagingItemDeclarationOfConformityRequirements,
  type PackagingItemDeclarationOfConformityRequirementKey,
} from './lib/packagingItemDeclarationOfConformityRequirements'
import { PackagingItemDeclarationOfConformityExportDialog } from './PackagingItemDeclarationOfConformityExportDialog'

const REQUIREMENT_LABEL_KEYS: Record<
  PackagingItemDeclarationOfConformityRequirementKey,
  string
> = {
  manufacturerIdentity:
    'itemDetail.declarationOfConformity.requirements.manufacturerIdentity',
  declarationObject:
    'itemDetail.declarationOfConformity.requirements.declarationObject',
  heavyMetalsEvidence:
    'itemDetail.declarationOfConformity.requirements.heavyMetalsEvidence',
  substancesOfConcernEvidence:
    'itemDetail.declarationOfConformity.requirements.substancesOfConcernEvidence',
  itemExportable:
    'itemDetail.declarationOfConformity.requirements.itemExportable',
}

type PackagingItemDeclarationOfConformityExportButtonProps = {
  packagingSupplierId: string
  item: PackagingItem
}

export function PackagingItemDeclarationOfConformityExportButton({
  packagingSupplierId,
  item,
}: PackagingItemDeclarationOfConformityExportButtonProps) {
  const { t } = useTranslation('packagingSuppliers')
  const canWriteOrgData = useCanWriteOrgData()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [progressMessage, setProgressMessage] = useState<string | null>(null)

  const { requirements, exportReady, unmetKeysExceptManufacturer } =
    evaluatePackagingItemDeclarationOfConformityRequirements(item)

  const isGenerating = progressMessage != null
  const showReadOnlyTooltip = !canWriteOrgData
  const showRequirementsTooltip = canWriteOrgData && !exportReady

  const checklist = (
    <ul className='space-y-1.5 text-left text-sm'>
      {requirements.map((requirement) => {
        const Icon = requirement.met ? CheckCircle2 : Circle
        return (
          <li
            key={requirement.key}
            className={cn(
              'flex items-start gap-2',
              requirement.met ? 'text-muted-foreground' : 'text-foreground'
            )}
          >
            <Icon
              className={cn(
                'mt-0.5 size-4 shrink-0',
                requirement.met ? 'text-emerald-600' : 'text-muted-foreground'
              )}
              aria-hidden
            />
            <span>{t(REQUIREMENT_LABEL_KEYS[requirement.key])}</span>
          </li>
        )
      })}
    </ul>
  )

  let buttonLabel = t('itemDetail.declarationOfConformity.export')
  if (isGenerating && progressMessage) {
    buttonLabel = progressMessage
  }
  if (isGenerating && !progressMessage) {
    buttonLabel = t('itemDetail.declarationOfConformity.exporting')
  }

  const button = (
    <Button
      type='button'
      variant='outline'
      size='sm'
      disabled={!canWriteOrgData || !exportReady || isGenerating}
      onClick={() => {
        if (!canWriteOrgData || !exportReady) {
          return
        }
        setDialogOpen(true)
      }}
    >
      {isGenerating ? (
        <Loader2 className='size-3.5 animate-spin' />
      ) : (
        <FilePlus2 className='size-3.5' />
      )}
      {buttonLabel}
    </Button>
  )

  let buttonWithHint = button
  if (showReadOnlyTooltip) {
    buttonWithHint = (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-flex' tabIndex={0}>
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent side='bottom' align='end' className='max-w-sm'>
          <p>{t('itemDetail.declarationOfConformity.readOnlyHint')}</p>
        </TooltipContent>
      </Tooltip>
    )
  }
  if (showRequirementsTooltip) {
    buttonWithHint = (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-flex' tabIndex={0}>
            {button}
          </span>
        </TooltipTrigger>
        <TooltipContent side='bottom' align='end' className='max-w-sm'>
          <p className='mb-2 font-medium'>
            {t('itemDetail.declarationOfConformity.requirementsTitle')}
          </p>
          {checklist}
          {unmetKeysExceptManufacturer.length > 0 && (
            <p className='mt-2 text-xs text-muted-foreground'>
              {t('itemDetail.declarationOfConformity.requirementsHint')}
            </p>
          )}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <>
      {buttonWithHint}
      <PackagingItemDeclarationOfConformityExportDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        packagingSupplierId={packagingSupplierId}
        itemId={item.id}
        onProgressMessage={setProgressMessage}
      />
    </>
  )
}
